import React, { useEffect, useRef } from 'react'
import { Typography } from '@mui/material'
import { StandardModal } from 'components'
import { Topic } from './Help.vm'

interface VideoModalProps {
    open: boolean
    onClose: () => void
    topic: Topic
}

interface VideoProps {
    src: string
    type: string
    className?: string
    autoPlay?: boolean
    videoRef?: React.RefObject<HTMLVideoElement>
}

// Contain a html5 video with a single src
const Video = ({
    src,
    type,
    className = '',
    autoPlay = false,
    videoRef = undefined,
}: VideoProps): React.ReactElement => (
    <div
        style={{
            position: 'relative',
            width: '100%',
            paddingBottom: '56.25%', // 16:9 aspect ratio (9/16 = 0.5625)
            height: 0,
            borderRadius: '8px',
            overflow: 'hidden',
        }}
    >
        <video
            className={`${className} emp-video`}
            controls
            autoPlay={autoPlay}
            muted
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: '8px',
            }}
            ref={videoRef}
        >
            <source src={src} type={type} />
            <track kind="captions" srcLang="en" label="English" />
            Unable to view the video in this browser, please try with another.
        </video>
    </div>
)

const VideoModal = ({
    open,
    onClose,
    topic,
}: VideoModalProps): React.ReactElement => {
    const videoEl = useRef<HTMLVideoElement>(null)

    useEffect(() => {
        const el = videoEl.current
        if (!el) return
        if (open) {
            // Ensure video starts muted and try to autoplay when the modal opens
            el.muted = true
            const tryPlay = async () => {
                try {
                    await el.play()
                } catch {
                    // Autoplay might be blocked; noop since we're already muted
                    /* noop */
                }
            }
            tryPlay()
        } else {
            // Pause and reset when modal closes
            el.pause()
            try {
                el.currentTime = 0
            } catch {
                /* noop */
            }
        }
    }, [open])

    return (
        <StandardModal
            openModal={open}
            onModalClose={onClose}
            modalHeaderTitle={`${topic.title}`}
            testId="video-modal"
            maxModalWidth="lg"
            enablePrimaryActionBtn={false}
            enableSecondaryActionBtn={false}
            disableBackdropClick={false}
        >
            {topic?.videoUrl && topic?.videoType ? (
                <Video
                    src={`${process.env.PUBLIC_URL}/help/${topic.videoUrl}`}
                    type={topic.videoType}
                    autoPlay={open}
                    videoRef={videoEl}
                />
            ) : (
                <Typography variant="body1" color="textSecondary">
                    No video available for this topic.
                </Typography>
            )}
        </StandardModal>
    )
}

export default VideoModal
